import { useMemo } from 'react'

import { useCurrentOrgId, useIsCloaking } from '~/store/slices/currentOrg'

import type { BatteryModel } from '../../client/ders'
import useManagedSites from './sites'
import useDataManager from './useDataManager'
import { EMPTY, useNormalizedFilters } from './utils'

type BatteryFilters = {
  orgId?: string
  siteId?: string
  nodeId?: string
}

type UseManagedBatteriesInput = {
  filters?: BatteryFilters
}

export default function useManagedBatteries({ filters }: UseManagedBatteriesInput = {}) {
  const { batteries } = useDataManager()
  const { data, isLoading, isFetching } = batteries

  // The manager fetches with `orgIds: [principalOrgId]` + a deep orgDepth,
  // so the cached set is already scoped to the principal user's org tree.
  // The site-join only does real narrowing when the caller wants a sub-slice
  // — i.e. while cloaking, or when an explicit `orgId` filter is passed.
  // Skipping the join (and its loading wait) on the common no-narrowing path
  // keeps the page snappy without sacrificing correctness when narrowing is
  // actually needed.
  const isCloaking = useIsCloaking()
  const needsSiteJoin = isCloaking || filters?.orgId != null

  const { sites: orgSites, isLoading: isSitesLoading, isFetching: isSitesFetching } = useManagedSites({
    filters: { orgId: filters?.orgId }
  })

  return {
    batteries: useFilteredBatteries(data, orgSites, needsSiteJoin, filters),
    isLoading: isLoading || (needsSiteJoin && isSitesLoading),
    isFetching: isFetching || (needsSiteJoin && isSitesFetching)
  }
}

function useFilteredBatteries(
  data: BatteryModel[],
  orgSites: ReturnType<typeof useManagedSites>['sites'],
  needsSiteJoin: boolean,
  filters?: BatteryFilters
) {
  const currentOrgId = useCurrentOrgId()

  const normalizedFilters = useNormalizedFilters(filters, {
    orgId: currentOrgId || undefined
  })

  const allowedSiteIds = useMemo(() => new Set(orgSites.map((site) => site.depotId)), [orgSites])

  return useMemo(() => {
    const { siteId, nodeId } = normalizedFilters

    if (siteId === EMPTY || nodeId === EMPTY) {
      return []
    }

    let filtered = needsSiteJoin
      ? data.filter((battery) => allowedSiteIds.has(battery.siteId))
      : data

    if (siteId != null) {
      filtered = filtered.filter((battery) => battery.siteId === siteId)
    }

    if (nodeId != null) {
      filtered = filtered.filter((battery) => battery.nodeId === nodeId)
    }

    return filtered
  }, [data, allowedSiteIds, needsSiteJoin, normalizedFilters])
}
